/**
 * DYNAMIC DETAIL PAGE RENDERER — Displays a record's details based on field definitions.
 * Includes audit metadata (created_at, updated_at, record id, module key).
 */
import React from 'react';
import {
  Box, Grid, Typography, Card, CardContent, Stack, Chip, Divider,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const CURRENCY_SYMBOL = '\u20ac';

function formatDate(iso) {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleDateString('sq-AL', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function DynamicDetailRenderer({
  fields = [],
  sections = [],
  values = {},
  record = null, // full record object with created_at, updated_at, id, module_key
  moduleKey = '',
}) {
  const visibleFields = fields
    .filter(f => f.is_active && f.show_in_detail)
    .sort((a, b) => a.sort_order - b.sort_order);

  const activeSections = sections
    .filter(s => s.is_active)
    .sort((a, b) => a.sort_order - b.sort_order);

  const formatValue = (field, value) => {
    if (value === null || value === undefined || value === '') return '\u2014';

    switch (field.field_type) {
      case 'boolean':
      case 'checkbox':
        return value === true || value === 'true'
          ? <Chip label="Po" size="small" color="success" variant="outlined" />
          : <Chip label="Jo" size="small" color="default" variant="outlined" />;
      case 'date':
        try {
          return new Date(value + 'T12:00:00').toLocaleDateString('sq-AL', {
            year: 'numeric', month: 'long', day: 'numeric',
          });
        } catch { return value; }
      case 'currency': {
        const n = Number(value);
        return isNaN(n) ? value : `${n.toLocaleString('de-DE', { minimumFractionDigits: 2 })} ${CURRENCY_SYMBOL}`;
      }
      case 'number': {
        const n = Number(value);
        return isNaN(n) ? value : n.toLocaleString('de-DE');
      }
      case 'select':
      case 'radio': {
        const opt = (field.options || []).find(o => o.value === value);
        return opt
          ? <Chip label={opt.label} size="small" variant="outlined" sx={opt.color ? { borderColor: opt.color, color: opt.color } : {}} />
          : value;
      }
      case 'multiselect': {
        const vals = Array.isArray(value) ? value : (value || '').split(',').filter(Boolean);
        if (vals.length === 0) return '\u2014';
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {vals.map(v => {
              const opt = (field.options || []).find(o => o.value === v);
              return <Chip key={v} label={opt?.label || v} size="small" variant="outlined" />;
            })}
          </Stack>
        );
      }
      case 'email':
        return <a href={`mailto:${value}`} style={{ color: '#3E8FA3', textDecoration: 'none' }}>{value}</a>;
      case 'phone':
        return <a href={`tel:${value}`} style={{ color: '#3E8FA3', textDecoration: 'none' }}>{value}</a>;
      case 'textarea':
        return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>;
      default:
        return String(value);
    }
  };

  const renderField = (field) => {
    const value = values[field.key];
    const width = parseInt(field.width) || 6;

    return (
      <Grid item xs={12} sm={Math.min(12, width)} key={field.key}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.03em' }}>
            {field.label}
          </Typography>
          <Typography variant="body2" sx={{ minHeight: 20 }}>
            {formatValue(field, value)}
          </Typography>
        </Box>
      </Grid>
    );
  };

  // Group fields by section
  const sectionedFields = {};
  const unsectionedFields = [];
  for (const f of visibleFields) {
    if (f.section_id) {
      if (!sectionedFields[f.section_id]) sectionedFields[f.section_id] = [];
      sectionedFields[f.section_id].push(f);
    } else {
      unsectionedFields.push(f);
    }
  }

  return (
    <Box>
      {activeSections.map(section => {
        const sFields = sectionedFields[section.id] || [];
        if (sFields.length === 0) return null;
        return (
          <Card key={section.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ p: '8px 16px', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700}>{section.label}</Typography>
            </Box>
            <CardContent>
              <Grid container spacing={2.5}>
                {sFields.map(f => renderField(f))}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      {unsectionedFields.length > 0 && (
        <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent>
            <Grid container spacing={2.5}>
              {unsectionedFields.map(f => renderField(f))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {visibleFields.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Asnj\u00eb fush\u00eb e konfiguruar p\u00ebr pamjen e detajeve.
        </Typography>
      )}

      {/* Audit Metadata */}
      {record && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={3} alignItems="center" sx={{ px: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>ID:</strong> {record.id}
              </Typography>
              {moduleKey && (
                <Typography variant="caption" color="text.secondary">
                  <strong>Moduli:</strong> {moduleKey}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                <strong>Krijuar:</strong> {formatDate(record.created_at)}
              </Typography>
              {record.updated_at && record.updated_at !== record.created_at && (
                <Typography variant="caption" color="text.secondary">
                  <strong>P\u00ebrditësuar:</strong> {formatDate(record.updated_at)}
                </Typography>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Box>
  );
}
